import React, { FC, useContext, useMemo, useState, useCallback } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
	Grid,
	Skeleton,
	Stack,
	useTheme,
	Card,
	CardHeader,
	CardContent,
	ToggleButtonGroup,
	ToggleButton,
	Box,
	alpha,
	Button,
	TextField,
	FormControl,
	useMediaQuery,
	Chip,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

import { Procedure } from "@/model/Procedure/Procedure";
import { UserContext } from "@/model/User/UserContext";

import {
	ProcedureQueryKeys,
	useFetchActiveProcedureGroupFolders,
	useFetchProcedures,
} from "@/service/ProcedureQueries";
import { useFetchProcedureUpdateSuggestions } from "@/service/TaskQueries";
import { useFetchUsers } from "@/service/ApplicationQueries";
import { useFetchArchivedProcedures } from "@/service/ArchiveQueries";
import useDataFilters from "../Archive/Hooks/useDateFilters";
import ProceduresInsightsDialog from "./ProceduresInsightsDialog";
import { SATOSLink } from "../Application/SATOSLink";
import { Translate } from "../Application/Translate";

// dayjs plugins at module level
dayjs.extend(relativeTime);
dayjs.extend(isoWeek);
dayjs.extend(utc);

const parseDate = (v?: string | null) => (v ? dayjs(v) : null);
const isRealDate = (d: dayjs.Dayjs | null) => !!d && d.isValid() && d.year() > 2000;

type Granularity = "daily" | "weekly" | "monthly";

type ArchivedProject = {
	Id: number;
	CompanyId: number;
	Name: string;
	Description?: string;
	Created: string;
	Updated?: string;
	FileCreated?: boolean;
	ParentId?: number;
	ProcedureRevisionId?: number;
	Link?: string;
};

type SuggestionItem = {
	Id: number;
	StepId: number;
	Created: string;
	CreatedBy?: { Id: number; Name: string };
	Status?: string;
	Description?: string;
};

type SuggestionWithContext = SuggestionItem & {
	ProcedureId?: number;
	ProcedureName?: string;
	ChecklistId?: number;
	ChecklistName?: string;
};

const formatBucketKey = (d: dayjs.Dayjs, g: Granularity) => {
	if (g === "daily") return d.format("YYYY-MM-DD");
	if (g === "weekly") {
		const isoYear = d.isoWeekYear();
		const isoWeekNum = d.isoWeek();
		return `${isoYear}-W${String(isoWeekNum).padStart(2, "0")}`;
	}
	return d.format("YYYY-MM");
};

const startOfBucket = (d: dayjs.Dayjs, g: Granularity) => {
	if (g === "daily") return d.startOf("day");
	if (g === "weekly") return d.startOf("isoWeek");
	return d.startOf("month");
};

const endOfBucket = (d: dayjs.Dayjs, g: Granularity) => {
	if (g === "daily") return d.endOf("day");
	if (g === "weekly") return d.endOf("isoWeek");
	return d.endOf("month");
};

const ProceduresOverview: FC<{
	initialRangeDays?: 90 | 180 | 365 | 730;
	height?: number;
}> = ({ initialRangeDays = 365, height = 520 }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const user = useContext(UserContext);

	const { isLoading: isProceduresLoading, data: procedures = [] } =
		useFetchProcedures(ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc);
	const { groupList } = useFetchActiveProcedureGroupFolders();
	const { data: suggestionsData, isLoading: isLoadingSuggestions } =
		useFetchProcedureUpdateSuggestions(false);
	const { data: usersAll = [] } = useFetchUsers();
	const { dataFilters } = useDataFilters();
	const { data: archivedProcedures, isLoading: isLoadingArchive } =
		useFetchArchivedProcedures(dataFilters);

	const [showInsights, setShowInsights] = useState(false);
	const [granularity, setGranularity] = useState<Granularity>("weekly");

	// you wanted the 2 x datetime-local approach in UTC
	const [startFromUtc, setStartFromUtc] = useState(
		dayjs().subtract(initialRangeDays, "day").utc().format()
	);
	const [endAtUtc, setEndAtUtc] = useState(dayjs().utc().format());

	// derived range for charts
	const rangeStart = dayjs(startFromUtc);
	const rangeEnd = dayjs(endAtUtc);

	const archivedProjects: ArchivedProject[] = useMemo(() => {
		const list = (archivedProcedures?.ArchivedProjects ?? []) as ArchivedProject[];
		return list
			.filter((p) => !!p?.Created)
			.map((p) => ({ ...p, Name: (p?.Name || "").trim() }));
	}, [archivedProcedures]);

	type Bucket = { key: string; date: dayjs.Dayjs };

	const makeBuckets = (g: Granularity): Bucket[] => {
		const buckets: Bucket[] = [];
		let cursor = startOfBucket(rangeStart, g);
		const end = endOfBucket(rangeEnd, g);

		while (cursor.isBefore(end) || cursor.isSame(end)) {
			buckets.push({ key: formatBucketKey(cursor, g), date: cursor });
			cursor = cursor.add(1, g === "daily" ? "day" : g === "weekly" ? "week" : "month");
		}
		return buckets;
	};

	const buckets = useMemo(
		() => makeBuckets(granularity),
		[granularity, rangeStart, rangeEnd]
	);

	// A. archived usage time series
	const timeSeries = useMemo(() => {
		const counts = new Map<string, number>();
		const names = new Map<string, string[]>();

		for (const b of buckets) {
			counts.set(b.key, 0);
			names.set(b.key, []);
		}

		for (const ap of archivedProjects) {
			const d = dayjs(ap.Created);
			if (d.isBefore(rangeStart) || d.isAfter(rangeEnd)) continue;
			const key = formatBucketKey(d, granularity);
			if (!counts.has(key)) continue;
			counts.set(key, (counts.get(key) ?? 0) + 1);
			const nm = (ap.Name || "").trim();
			if (nm) names.get(key)!.push(nm);
		}

		const x: string[] = [];
		const y: number[] = [];
		const labelMap: Record<string, string[]> = {};

		for (const b of buckets) {
			x.push(b.key);
			y.push(counts.get(b.key) ?? 0);
			labelMap[b.key] = (names.get(b.key) ?? []).slice().sort((a, b) => a.localeCompare(b));
		}

		return { x, y, labelMap };
	}, [archivedProjects, buckets, granularity, rangeStart, rangeEnd]);

	// B. usage by name
	const usageByName = useMemo(() => {
		const map = new Map<string, number>();
		for (const ap of archivedProjects) {
			const name = ap.Name?.trim();
			if (!name) continue;
			map.set(name, (map.get(name) ?? 0) + 1);
		}
		for (const p of procedures as Procedure[]) {
			const name = (p?.Name || "").trim();
			if (!name) continue;
			if (!map.has(name)) map.set(name, 0);
		}
		const entries = Array.from(map.entries()).map(([name, count]) => ({ name, count }));
		const most = entries
			.filter((e) => e.name)
			.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
			.slice(0, 10);
		const least = entries
			.filter((e) => e.name)
			.sort((a, b) => a.count - b.count || a.name.localeCompare(b.name))
			.slice(0, 10);
		return { most, least };
	}, [archivedProjects, procedures]);

	// C. suggestions flattened
	const suggestions = useMemo(() => {
		const list = (suggestionsData ?? []) as Array<{
			ProcedureId: number;
			ProcedureName: string;
			Checklists: Array<{
				ChecklistId: number;
				ChecklistName: string;
				ProcedureUpdateSuggestions: SuggestionItem[];
			}>;
		}>;
		const all: SuggestionWithContext[] = [];
		for (const proc of list) {
			for (const ch of proc.Checklists || []) {
				for (const s of ch.ProcedureUpdateSuggestions || []) {
					const d = parseDate(s.Created);
					if (!isRealDate(d)) continue;
					all.push({
						...s,
						ProcedureId: proc.ProcedureId,
						ProcedureName: proc.ProcedureName,
						ChecklistId: ch.ChecklistId,
						ChecklistName: ch.ChecklistName,
					});
				}
			}
		}
		return all;
	}, [suggestionsData]);

	// D. suggestions over time
	const updatesTimeSeries = useMemo(() => {
		const counts = new Map<string, number>();
		const texts = new Map<string, string[]>();

		for (const b of buckets) {
			counts.set(b.key, 0);
			texts.set(b.key, []);
		}

		for (const it of suggestions) {
			const d = dayjs(it.Created);
			if (d.isBefore(rangeStart) || d.isAfter(rangeEnd)) continue;
			const key = formatBucketKey(d, granularity);
			if (!counts.has(key)) continue;
			counts.set(key, (counts.get(key) ?? 0) + 1);
			const t =
				it.Description ||
				`${it.ProcedureName || "?"} / ${it.ChecklistName || "?"} / #${it.Id}`;
			texts.get(key)!.push(t);
		}

		const x: string[] = [];
		const y: number[] = [];
		const labelMap: Record<string, string[]> = {};
		for (const b of buckets) {
			x.push(b.key);
			y.push(counts.get(b.key) ?? 0);
			labelMap[b.key] = (texts.get(b.key) ?? []).slice();
		}
		return { x, y, labelMap };
	}, [suggestions, buckets, granularity, rangeStart, rangeEnd]);

	// Procedure updates over time (counts procedures by their latest Updated/LastModified/Created)
	const procedureUpdatesTimeSeries = useMemo(() => {
		const counts = new Map<string, number>();
		const names = new Map<string, string[]>();

		for (const b of buckets) {
			counts.set(b.key, 0);
			names.set(b.key, []);
		}

		for (const p of procedures as Procedure[]) {
			const d = [p.Updated, (p as any).LastModified, p.Created]
				.map((v) => parseDate(v))
				.find((x) => isRealDate(x));
			if (!d) continue;
			if (d.isBefore(rangeStart) || d.isAfter(rangeEnd)) continue;
			const key = formatBucketKey(d, granularity);
			if (!counts.has(key)) continue;
			counts.set(key, (counts.get(key) ?? 0) + 1);
			const name = ((p as any).Name || `#${(p as any).Id}`).trim();
			if (name) names.get(key)!.push(name);
		}

		const x: string[] = [];
		const y: number[] = [];
		const labelMap: Record<string, string[]> = {};
		for (const b of buckets) {
			x.push(b.key);
			y.push(counts.get(b.key) ?? 0);
			labelMap[b.key] = (names.get(b.key) ?? []).slice().sort((a, b) => a.localeCompare(b));
		}
		return { x, y, labelMap };
	}, [procedures, buckets, granularity, rangeStart, rangeEnd]);


	// E. suggestions per status
	const suggestionStatusSeries = useMemo(() => {
		const statuses = ["Closed", "Verified", "Unverified", "Declined"];
		const result: Record<
			string,
			{
				x: string[];
				y: number[];
				labelMap: Record<string, string[]>;
			}
		> = {};

		for (const status of statuses) {
			const counts = new Map<string, number>();
			const labels = new Map<string, string[]>();

			for (const b of buckets) {
				counts.set(b.key, 0);
				labels.set(b.key, []);
			}

			for (const s of suggestions) {
				const sStatus = (s.Status || "").trim();
				if (sStatus !== status) continue;
				const d = dayjs(s.Created);
				if (d.isBefore(rangeStart) || d.isAfter(rangeEnd)) continue;
				const key = formatBucketKey(d, granularity);
				if (!counts.has(key)) continue;
				counts.set(key, (counts.get(key) ?? 0) + 1);
				const label =
					s.Description ||
					`${s.ProcedureName || "?"} / ${s.ChecklistName || "?"} / #${s.Id}`;
				labels.get(key)!.push(label);
			}

			const x: string[] = [];
			const y: number[] = [];
			const labelMap: Record<string, string[]> = {};
			for (const b of buckets) {
				x.push(b.key);
				y.push(counts.get(b.key) ?? 0);
				labelMap[b.key] = (labels.get(b.key) ?? []).slice();
			}
			result[status] = { x, y, labelMap };
		}

		return result;
	}, [suggestions, buckets, granularity, rangeStart, rangeEnd]);

	// F. recent updated
	const recentUpdated = useMemo(() => {
		type Proc = Procedure & {
			Updated?: string | null;
			LastModified?: string | null;
			Created?: string | null;
		};
		const rows = (procedures as Proc[]).map((p) => {
			const d = [p.Updated, (p as any).LastModified, p.Created]
				.map((v) => parseDate(v))
				.find((x) => isRealDate(x));
			return {
				id: p.Id,
				name: (p.Name || "").trim() || `#${p.Id}`,
				date: d,
			};
		});

		return rows
			.filter((r) => isRealDate(r.date))
			.sort((a, b) => (b.date as any).valueOf() - (a.date as any).valueOf())
			.slice(0, 10)
			.map((r) => ({
				name: r.name,
				dateStr: (r.date as dayjs.Dayjs).format("YYYY-MM-DD HH:mm"),
			}));
	}, [procedures]);

	// G. cumulative
	const cumulativeSeries = useMemo(() => {
		type Proc = Procedure & {
			Updated?: string | null;
			LastModified?: string | null;
			Created?: string | null;
		};
		const validDates: dayjs.Dayjs[] = [];
		for (const p of procedures as Proc[]) {
			const d = [p.Created, p.Updated, (p as any).LastModified]
				.map((v) => parseDate(v))
				.find((x) => isRealDate(x));
			if (isRealDate(d)) validDates.push(d as dayjs.Dayjs);
		}
		const x = buckets.map((b) => b.key);
		if (!validDates.length) return { x, y: x.map(() => 0), adds: x.map(() => 0) };

		const addsMap = new Map<string, number>();
		for (const b of buckets) addsMap.set(b.key, 0);

		validDates.sort((a, b) => a.valueOf() - b.valueOf());
		for (const d of validDates) {
			if (d.isAfter(rangeEnd)) continue;
			const key = formatBucketKey(d, granularity);
			if (!addsMap.has(key)) continue;
			addsMap.set(key, (addsMap.get(key) ?? 0) + 1);
		}

		let running = 0;
		const y: number[] = [];
		const adds: number[] = [];
		for (const b of buckets) {
			const add = addsMap.get(b.key) ?? 0;
			running += add;
			y.push(running);
			adds.push(add);
		}
		return { x, y, adds };
	}, [procedures, buckets, granularity, rangeEnd]);

	// KPIs
	const archivedInRange = timeSeries.y.reduce((a, b) => a + b, 0);
	const suggestionsInRange = updatesTimeSeries.y.reduce((a, b) => a + b, 0);
	const totalProcedures = (procedures as Procedure[]).length;

	// chart options
	const colorArchived = theme.palette.primary.main;
	const colorSuggestions = theme.palette.secondary.main;
	const colorUpdates = theme.palette.info.main;
	const colorCumulative = theme.palette.success.main;

	const combinedActivityOption = useMemo((): echarts.EChartsOption => {
		return {
			backgroundColor: "transparent",
			color: [colorArchived, colorSuggestions, colorUpdates],
			grid: { left: 42, right: 16, top: 40, bottom: 48 },
			legend: {
				top: 4,
			},
			tooltip: {
				trigger: "axis",
				axisPointer: { type: "cross" },
				borderRadius: 8,
				formatter: (params: any) => {
					const list = Array.isArray(params) ? params : [params];
					const key = list[0]?.axisValue;
					const archivedNames = timeSeries.labelMap[key] || [];
					const suggestionsNames = updatesTimeSeries.labelMap[key] || [];
					const procUpdateNames = procedureUpdatesTimeSeries.labelMap[key] || [];
					const trunc = (s: string, max = 60) =>
						s.length > max ? s.slice(0, max) + "…" : s;
					return `
          <div style="max-width:340px">
            <div style="font-weight:600; margin-bottom:6px">${key}</div>
            ${list
							.map(
								(p: any) =>
									`<div style="display:flex; justify-content:space-between; gap:12px;">
                    <span>${p.marker}${p.seriesName}</span>
                    <span style="font-weight:600">${p.data ?? 0}</span>
                  </div>`
							)
							.join("")}
            ${procUpdateNames.length
							? `<div style="margin-top:6px; font-size:12px;">
                  <div style="font-weight:500;">Procedure updates:</div>
                  ${procUpdateNames
								.slice(0, 10)
								.map((n) => `• ${trunc(n)}`)
								.join("<br/>")}
                  ${procUpdateNames.length > 10 ? `<div style="color:#888">… and ${procUpdateNames.length - 10} more</div>` : ""}
                </div>`
							: ""
						}
            ${archivedNames.length
							? `<div style="margin-top:6px; font-size:12px;">
                  <div style="font-weight:500;">Archived:</div>
                  ${archivedNames.slice(0, 10).map((n) => `• ${trunc(n)}`).join("<br/>")}
                  ${archivedNames.length > 10 ? `<div style="color:#888">… and ${archivedNames.length - 10} more</div>` : ""}
                </div>`
							: ""
						}
            ${suggestionsNames.length
							? `<div style="margin-top:6px; font-size:12px;">
                  <div style="font-weight:500;">Suggestions:</div>
                  ${suggestionsNames.slice(0, 10).map((n) => `• ${trunc(n)}`).join("<br/>")}
                  ${suggestionsNames.length > 10 ? `<div style="color:#888">… and ${suggestionsNames.length - 10} more</div>` : ""}
                </div>`
							: ""
						}
          </div>
        `;
				},
			},
			xAxis: {
				type: "category",
				data: timeSeries.x,
				axisLabel: { rotate: granularity === "monthly" ? 0 : 38 },
				boundaryGap: false,
			},
			yAxis: [
				{
					type: "value",
					minInterval: 1,
				},
			],
			dataZoom:
				timeSeries.x.length > 30 ? [{ type: "inside" }, { type: "slider", height: 20 }] : undefined,
			series: [
				{
					name: "Archived procedures",
					type: "line",
					smooth: true,
					symbol: "circle",
					symbolSize: 4,
					areaStyle: { opacity: 0.16 },
					data: timeSeries.y,
				},
				{
					name: "Procedure improvement suggestions",
					type: "bar",
					barMaxWidth: 20,
					data: updatesTimeSeries.y,
				},
				{
					name: "Procedure updates",
					type: "line",
					smooth: true,
					symbol: "circle",
					symbolSize: 4,
					lineStyle: { type: "dashed" },
					areaStyle: { opacity: 0.08 },
					data: procedureUpdatesTimeSeries.y,
				},
			],
		};
	}, [timeSeries, updatesTimeSeries, procedureUpdatesTimeSeries, granularity, colorArchived, colorSuggestions, colorUpdates]);


	const topUsedOption = useMemo((): echarts.EChartsOption => {
		return {
			backgroundColor: "transparent",
			color: [theme.palette.primary.main],
			grid: { left: 160, right: 16, top: 40, bottom: 16 },
			tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
			xAxis: { type: "value", minInterval: 1 },
			yAxis: {
				type: "category",
				data: usageByName.most.map((r) => r.name),
				axisLabel: { interval: 0 },
				inverse: true, // 👈 make the first (most used) show at the top
			},
			series: [
				{
					name: "Archived count",
					type: "bar",
					data: usageByName.most.map((r) => r.count),
					label: { show: true, position: "right" },
					barMaxWidth: 18,
				},
			],
		};
	}, [usageByName, theme.palette.primary.main]);


	const cumulativeOption = useMemo((): echarts.EChartsOption => {
		const many = cumulativeSeries.x.length > 30;
		return {
			backgroundColor: "transparent",
			color: [colorCumulative],
			grid: { left: 48, right: 16, top: 16, bottom: many ? 72 : 40 },
			tooltip: {
				trigger: "axis",
				formatter: (params: any) => {
					const p = Array.isArray(params) ? params[0] : params;
					const idx = p?.dataIndex ?? 0;
					const added = (cumulativeSeries as any).adds?.[idx] ?? 0;
					const total = p?.data ?? 0;
					return `<div style="max-width:320px">
            <div><strong>${p.axisValue}</strong></div>
            <div>Total procedures: <strong>${total}</strong></div>
            <div>Added this period: <strong>${added}</strong></div>
          </div>`;
				},
			},
			xAxis: {
				type: "category",
				data: cumulativeSeries.x,
				boundaryGap: false,
				axisLabel: { rotate: 40 },
			},
			yAxis: { type: "value", minInterval: 1 },
			dataZoom: many ? [{ type: "inside" }, { type: "slider", height: 18 }] : undefined,
			series: [
				{
					name: "Cumulative procedures",
					type: "line",
					smooth: true,
					symbol: "circle",
					symbolSize: 6,
					areaStyle: { opacity: 0.08 },
					data: cumulativeSeries.y,
					emphasis: { focus: "series" },
				},
			],
		};
	}, [cumulativeSeries, colorCumulative]);

	const statusLineOption = (
		title: string,
		series: { x: string[]; y: number[]; labelMap: Record<string, string[]> },
		color: string
	): echarts.EChartsOption => {
		return {
			backgroundColor: "transparent",
			color: [color],
			title: {
				text: title,
				left: 0,
				textStyle: { fontSize: 13, fontWeight: 500 },
			},
			grid: { left: 40, right: 12, top: 46, bottom: 40 },
			tooltip: {
				trigger: "axis",
				borderRadius: 8,
				formatter: (params: any) => {
					const p = Array.isArray(params) ? params[0] : params;
					const key = p?.axisValue as string;
					const labels = series.labelMap[key] || [];
					const shown = labels.slice(0, 12);
					const more = labels.length - shown.length;
					const trunc = (s: string) => (s.length > 70 ? s.slice(0, 70) + "…" : s);
					return `
            <div style="max-width:320px">
              <div style="font-weight:600;">${key}</div>
              <div style="margin:4px 0;">Count: <strong>${p?.data ?? 0}</strong></div>
              ${shown.length
							? `<div style="max-height:110px; overflow-y:auto;">
                      ${shown.map((t: string) => "• " + trunc(t)).join("<br/>")}
                    </div>`
							: "<i>No items</i>"
						}
              ${more > 0
							? `<div style="margin-top:3px; color:#999;">… and ${more} more</div>`
							: ""
						}
            </div>
          `;
				},
			},
			xAxis: {
				type: "category",
				data: series.x,
				axisLabel: { rotate: 36 },
				boundaryGap: false,
			},
			yAxis: { type: "value", minInterval: 1 },
			dataZoom:
				series.x.length > 30
					? [{ type: "inside" }, { type: "slider", height: 16 }]
					: undefined,
			series: [
				{
					type: "line",
					smooth: true,
					symbol: "circle",
					symbolSize: 4,
					areaStyle: { opacity: 0.08 },
					data: series.y,
				},
			],
		};
	};

	const isLoading = isProceduresLoading || isLoadingSuggestions || isLoadingArchive;


	const getStatusTitle = (status: typeof statuses[number]) => {
		switch (status) {
			case "Closed":
				return `${status} (on closed date)`;
			case "Verified":
				return `${status} (not yet closed)`;
			case "Unverified":
				return `${status} (still open)`;
			case "Declined":
				return `${status} (not yet closed)`;
			default:
				// fallback: keep previous behavior if a new/unknown status appears
				return `${status} (${suggestionStatusSeries[status]?.y.reduce((a, b) => a + b, 0) ?? 0})`;
		}
	};

	return (
		<Stack spacing={2} sx={{ width: "100%" }}>
			{/* HEADER / FILTER BAR */}
			<Card

				variant="outlined"
				sx={{
					paddingBottom: 2,
					borderRadius: 3,
					background: `linear-gradient(120deg, ${alpha(
						theme.palette.primary.main,
						0.06
					)}, ${alpha(theme.palette.background.paper, 1)})`,
				}}
			>
				<CardContent>
					<Stack
						direction={{ xs: "column", md: "row" }}
						spacing={2}
						alignItems={{ xs: "flex-start", md: "center" }}
						justifyContent="space-between"
						flexWrap="wrap"
					>
						{/* Left side: title + current range */}
						<Box>
							<Box sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
								<Translate String="Procedures.Overview.Title">Procedures overview</Translate>
							</Box>
							<Box sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
								{rangeStart.format("YYYY-MM-DD")} → {rangeEnd.format("YYYY-MM-DD")}
							</Box>
						</Box>

						{/* Right side: controls */}
						<Stack
							direction={{ xs: "column", md: "row" }}
							spacing={1}
							alignItems={{ xs: "stretch", md: "center" }}
							flexWrap="wrap"
							sx={{
								backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
								border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
								borderRadius: 2,
								p: 1,
								gap: 1,
							}}
						>
							{/* date inputs */}
							<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
								<FormControl sx={{ width: { xs: "100%", sm: 210 } }}>
									<TextField
										label={
											<Translate String="Procedures.Filters.StartDate">Start date</Translate>
										}
										type="datetime-local"
										value={dayjs(startFromUtc).format("YYYY-MM-DDTHH:mm")}
										onChange={(e) => setStartFromUtc(dayjs(e.target.value).utc().format())}
										size="small"
										InputLabelProps={{ shrink: true }}
									/>
								</FormControl>
								<FormControl sx={{ width: { xs: "100%", sm: 210 } }}>
									<TextField
										label={
											<Translate String="Procedures.Filters.EndDate">End date</Translate>
										}
										type="datetime-local"
										value={dayjs(endAtUtc).format("YYYY-MM-DDTHH:mm")}
										onChange={(e) => setEndAtUtc(dayjs(e.target.value).utc().format())}
										size="small"
										InputLabelProps={{ shrink: true }}
									/>
								</FormControl>
							</Stack>

							{/* row of small controls */}
							<Stack
								direction="row"
								spacing={1}
								alignItems="center"
								flexWrap="wrap"
								sx={{ minHeight: 36 }}
							>
								{/* presets */}
								<ToggleButtonGroup
									exclusive
									value={undefined}
									onChange={() => { }}
									size="small"
								>
									<ToggleButton
										value="90"
										onClick={() => {
											setStartFromUtc(dayjs().subtract(90, "day").utc().format());
											setEndAtUtc(dayjs().utc().format());
										}}
									>
										90d
									</ToggleButton>
									<ToggleButton
										value="180"
										onClick={() => {
											setStartFromUtc(dayjs().subtract(180, "day").utc().format());
											setEndAtUtc(dayjs().utc().format());
										}}
									>
										180d
									</ToggleButton>
									<ToggleButton
										value="365"
										onClick={() => {
											setStartFromUtc(dayjs().subtract(365, "day").utc().format());
											setEndAtUtc(dayjs().utc().format());
										}}
									>
										1y
									</ToggleButton>
									<ToggleButton
										value="730"
										onClick={() => {
											setStartFromUtc(dayjs().subtract(730, "day").utc().format());
											setEndAtUtc(dayjs().utc().format());
										}}
									>
										2y
									</ToggleButton>
								</ToggleButtonGroup>

								{/* granularity */}
								<ToggleButton value="daily">
									<Translate String="Procedures.Filters.Daily">Daily</Translate>
								</ToggleButton>
								<ToggleButton value="weekly">
									<Translate String="Procedures.Filters.Weekly">Weekly</Translate>
								</ToggleButton>
								<ToggleButton value="monthly">
									<Translate String="Procedures.Filters.Monthly">Monthly</Translate>
								</ToggleButton>


								<Button
									variant="contained"
									color="info"
									startIcon={<OpenInNewIcon fontSize="small" />}
									size="medium"
									onClick={() => setShowInsights(true)}
									aria-label="View insights"
									sx={{
										textTransform: "none",
										fontWeight: 700,
										fontSize: 13,
										px: 2,
										py: 0.75,
										borderRadius: 2,
									}}
								>
									<Translate String="Procedures.Button.ViewInsights">View insights</Translate>

								</Button>
							</Stack>
						</Stack>
					</Stack>


					{/* KPIs */}
					<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
						<Box
							sx={{
								flex: 1,
								p: 1.25,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.background.paper, 0.9),
								border: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
							}}
						>
							<Box sx={{ fontSize: 12, color: "text.secondary" }}>
								<Translate String="Procedures.KPI.Total">Total procedures</Translate>
							</Box>
							<Box sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
								{totalProcedures}
							</Box>
						</Box>
						<Box
							sx={{
								flex: 1,
								p: 1.25,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.background.paper, 0.9),
							}}
						>
							<Box sx={{ fontSize: 12, color: "text.secondary" }}>
								<Translate String="Procedures.KPI.ArchivedInRange">Archived in range</Translate>
							</Box>							<Box sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
								{archivedInRange}
							</Box>
						</Box>
						<Box
							sx={{
								flex: 1,
								p: 1.25,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.background.paper, 0.9),
							}}
						>
							<Translate String="Procedures.KPI.SuggestionsInRange">Suggestions in range</Translate>
							<Box sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
								{suggestionsInRange}
							</Box>
						</Box>
					</Stack>
				</CardContent>
			</Card>

			{isLoading ? (
				<Grid container spacing={2} alignItems="stretch">
					<Grid item xs={12}>
						<Skeleton variant="rounded" height={96} />
					</Grid>
					<Grid item xs={12} md={6}>
						<Skeleton variant="rounded" height={360} />
					</Grid>
					<Grid item xs={12} md={6}>
						<Skeleton variant="rounded" height={360} />
					</Grid>
					<Grid item xs={12}>
						<Skeleton variant="rounded" height={320} />
					</Grid>
				</Grid>
			) : (
				<Grid container spacing={2}>
					{/* LEFT COLUMN */}
					<Grid item xs={12} lg={8}>
						<Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
							<CardHeader
								title={
									<Translate String="Procedures.Chart.ActivityOverTime">Activity over time</Translate>
								}
								subheader={
									<Translate String="Procedures.Chart.ActivityOverTime.Sub">
										Archived procedures • Procedure updates • Suggestion improvments (by last edited)
									</Translate>
								} />
							<CardContent>
								<ReactECharts
									style={{ height }}
									notMerge
									opts={{ renderer: "canvas" }}
									option={combinedActivityOption}
								/>
							</CardContent>
						</Card>

						<Card variant="outlined" sx={{ borderRadius: 3 }}>
							<CardHeader
								title={
									<Translate String="Procedures.Chart.SuggestionsByStatus">Suggestions by status</Translate>
								} subheader={
									<Translate String="Procedures.Chart.SuggestionsByStatus.Sub">
										Uses selected granularity & date range
									</Translate>
								}
							/>
							<CardContent>
								<Grid container spacing={2}>
									{(["Closed", "Verified", "Unverified", "Declined"] as const).map(
										(status, idx) => (
											<Grid item xs={12} md={6} key={status}>
												<ReactECharts
													style={{ height: 260 }}
													notMerge
													opts={{ renderer: "canvas" }}
													option={statusLineOption(
														getStatusTitle(status),
														suggestionStatusSeries[status] || {
															x: buckets.map((b) => b.key),
															y: buckets.map(() => 0),
															labelMap: {},
														},
														[theme.palette.success.main,
														theme.palette.info.main,
														theme.palette.warning.main,
														theme.palette.error.main][idx % 4]
													)}
												/>
											</Grid>
										)
									)}
								</Grid>
							</CardContent>
						</Card>
					</Grid>

					{/* RIGHT COLUMN */}
					<Grid item xs={12} lg={4}>
						{/* top used */}
						<Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
							<CardHeader
								title={
									<Translate String="Procedures.Chart.TopUsed">
										Top 10 most used procedures
									</Translate>
								}
							/>							<CardContent>
								<Box sx={{ pt: 1, height: 450, pb: 1 }}>
									<ReactECharts
										style={{ height: "100%" }}
										notMerge
										opts={{ renderer: "canvas" }}
										option={topUsedOption}
									/>
								</Box>
							</CardContent>
						</Card>

						{/* recently updated */}
						<Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
							<CardHeader
								title={
									<Translate String="Procedures.Chart.RecentUpdated">
										Recently updated procedures
									</Translate>
								}
								subheader={
									<Translate String="Procedures.Chart.RecentUpdated.Sub">
										Based on Created/Updated/LastModified
									</Translate>
								}
							/>
							<CardContent>
								<Stack spacing={1.25}>
									{recentUpdated.map((r, idx) => {
										const proc = (procedures as Procedure[]).find(
											(p) => p.Name?.trim() === r.name.trim()
										);

										return (

											// inside render
											<SATOSLink
												target="_blank"
												key={idx}
												to="AccessProcedure"
												params={[proc?.Id ?? 0]}
												style={{ textDecoration: "none", color: "inherit" }}
											>
												<Stack
													direction="row"
													alignItems="center"
													justifyContent="space-between"
													sx={{
														borderRadius: 1,
														px: 1,
														py: 0.75,
														transition: "background 0.2s",
														cursor: "pointer",
														"&:hover": {
															background: alpha(theme.palette.primary.main, 0.06),
														},
														bgcolor: idx === 0 ? alpha(theme.palette.primary.main, 0.04) : "inherit",
														gap: 1,
													}}
												>
													{/* left: name (ellipsised) */}
													<Box
														sx={{
															pr: 1,
															minWidth: 0,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
															fontWeight: 500,
														}}
													>
														{r.name}
													</Box>

													<Box sx={{ color: "text.secondary", fontSize: 12, mr: 1 }}>
														{r.dateStr}
													</Box>

													<Chip
														size="small"
														clickable
														component="div"
														onClick={(e: React.MouseEvent) => {
															e.stopPropagation();
															seedSelection?.();
														}}
														icon={<OpenInNewIcon fontSize="small" />}
														sx={{
															borderRadius: 1.5,
															"& .MuiChip-label": { px: 0.75, fontWeight: 700 },

															ml: 1,
														}}
														label={<Translate String="Procedures.Button.Open">Open</Translate>}
														aria-label={
															<Translate String="Procedures.Button.OpenItem">
																Open {r.name}
															</Translate>
														}
													/>
												</Stack>
											</SATOSLink>

										);
									})}
								</Stack>
							</CardContent>
						</Card>

					</Grid>
					<Grid item xs={12}>

						{/* cumulative */}
						<Card variant="outlined" sx={{ borderRadius: 3 }}>
							<CardHeader
								title={
									<Translate String="Procedures.Chart.NumberOfProcedures">
										Number of procedures
									</Translate>
								}
							/>
							<CardContent sx={{ height: 240 }}>
								<ReactECharts
									style={{ height: "100%" }}
									notMerge
									opts={{ renderer: "canvas" }}
									option={cumulativeOption}
								/>
							</CardContent>
						</Card>
					</Grid>

					<Grid item xs={12}>
						<Box sx={{ fontSize: 12, color: "text.secondary", mt: 1 }}>
							<Translate String="Procedures.Chart.Notes">
								Notes: all charts reuse the granularity and date range controls at the top.
							</Translate>						</Box>
					</Grid>
				</Grid>
			)}

			{/* dialog */}
			<ProceduresInsightsDialog
				open={showInsights}
				onClose={() => setShowInsights(false)}
				procedures={procedures}
				archivedProjects={archivedProjects}
				suggestions={suggestions}
				currentUserId={user?.Id}
			/>

		</Stack>
	);
};

export default ProceduresOverview;
